import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import { getUserRole } from '../utils/roleHierarchy';
import { filterEntitiesByAccess } from '../utils/assignmentSystem';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import '../styles/ContentCreation.css';

const ContentCreation = ({ user }) => {
  const modal = useModal();
  // Get selected brand from global context
  const { selectedBrand: contextBrand, selectedOrg: contextOrg } = useOrgBrand();
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    caption: '',
    brand: '',
    selectedTags: [],
    notes: ''
  });
  const [availableBrands, setAvailableBrands] = useState([]);
  const [loading, setLoading] = useState(false);

  // COMMENTED OUT - Source selection (AI-based content generation)
  // const [showSourcesSection, setShowSourcesSection] = useState(false);
  // const [availableSources, setAvailableSources] = useState([]);
  // const [selectedSources, setSelectedSources] = useState([]);
  // const [loadingSources, setLoadingSources] = useState(false);
  // const [generatingContent, setGeneratingContent] = useState(false);

  const userRole = getUserRole(user);

  const availableTags = [
    { id: 'social', name: 'Social', color: '#3B82F6' },
    { id: 'blog', name: 'Blog', color: '#10B981' },
    { id: 'podcast', name: 'Podcast', color: '#8B5CF6' },
    { id: 'video', name: 'Video', color: '#EF4444' },
    { id: 'newsletter', name: 'Newsletter', color: '#F59E0B' },
    { id: 'promo', name: 'Promo', color: '#EC4899' },
  ];

  const contentTypes = [
    { id: 'instagram-post', name: 'Instagram Post', icon: 'instagram', color: '#E4405F', tags: ['social'], mediaTypes: ['Photo', 'Video'] },
    { id: 'instagram-story', name: 'Instagram Story', icon: 'instagram', color: '#C13584', tags: ['social'], mediaTypes: ['Photo', 'Video'] },
    { id: 'instagram-reel', name: 'Instagram Reel', icon: 'instagram', color: '#405DE6', tags: ['social', 'video'], mediaTypes: ['Video'] },
    { id: 'facebook-post', name: 'Facebook Post', icon: 'facebook', color: '#1877F2', tags: ['social'] },
    { id: 'twitter-post', name: 'Twitter/X Post', icon: 'twitter', color: '#000000', tags: ['social'] },
    { id: 'tiktok-video', name: 'TikTok Video', icon: 'play', color: '#000000', tags: ['social', 'video'], mediaTypes: ['Video'] },
    { id: 'youtube-video', name: 'YouTube Video', icon: 'youtube', color: '#FF0000', tags: ['video'], mediaTypes: ['Video'] },
    { id: 'youtube-short', name: 'YouTube Short', icon: 'youtube', color: '#FF0000', tags: ['social', 'video'], mediaTypes: ['Video'] },
    { id: 'linkedin-post', name: 'LinkedIn Post', icon: 'linkedin', color: '#0A66C2', tags: ['social'] },
    { id: 'blog-post', name: 'Blog Post', icon: 'doc', color: '#FF6B6B', tags: ['blog'] },
    { id: 'newsletter', name: 'Newsletter', icon: 'paper-plane-empty', color: '#F59E0B', tags: ['newsletter'] },
    { id: 'podcast-episode', name: 'Podcast Episode', icon: 'mic', color: '#8B5CF6', tags: ['podcast'] },
  ];

  useEffect(() => {
    if (selectedType) {
      fetchBrandsAndUsers();
      // COMMENTED OUT - No longer fetching sources for AI generation
      // fetchAllAccessibleSources();
    }
  }, [selectedType]);

  // Auto-fill brand from context when brands are loaded
  useEffect(() => {
    if (availableBrands.length > 0 && contextBrand && !formData.brand) {
      const matchingBrand = availableBrands.find(b =>
        (b.id === contextBrand._id || b.id === contextBrand.id) ||
        (b._id === contextBrand._id || b._id === contextBrand.id)
      );
      if (matchingBrand) {
        setFormData(prev => ({
          ...prev,
          brand: matchingBrand.id || matchingBrand._id
        }));
      }
    }
  }, [availableBrands, contextBrand]);

  const fetchBrandsAndUsers = async () => {
    setLoading(true);
    try {
      // Fetch brands based on user's assignments
      const brandsResponse = await axios.get('/api/brands/list');
      const brands = brandsResponse.data.brands || [];

      // Filter brands based on user role and assignments
      let accessibleBrands = brands;
      if (userRole === 'operator') {
        // Operators only see assigned brands
        const assignmentsResponse = await axios.get('/api/user/assignments');
        const userAssignments = assignmentsResponse.data.assignments;
        accessibleBrands = filterEntitiesByAccess(brands, userRole, userAssignments, 'brands');
      }

      // Sort brands alphabetically by name
      const sortedBrands = accessibleBrands.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setAvailableBrands(sortedBrands);
    } catch (error) {
      console.error('Error fetching brands and users:', error);
    } finally {
      setLoading(false);
    }
  };

  // COMMENTED OUT - Source-based AI content generation functions
  /*
  const fetchAllAccessibleSources = async () => {
    try {
      setLoadingSources(true);

      const [sourcesResponse, orgsResponse, brandsResponse] = await Promise.all([
        axios.get('/api/sources', { withCredentials: true }),
        axios.get('/api/user/organizations', { withCredentials: true }),
        axios.get('/api/brands/list', { withCredentials: true })
      ]);

      if (sourcesResponse.data.success) {
        const orgsData = orgsResponse.data.organizations || [];
        const brandsData = brandsResponse.data.brands || [];

        const enrichedSources = sourcesResponse.data.sources.map(source => {
          const sourceBrand = brandsData.find(b =>
            (b._id === source.brand_id) || (b.id === source.brand_id)
          );
          const sourceOrg = orgsData.find(o =>
            (o._id === source.organization_id) || (o.id === source.organization_id)
          );

          return {
            ...source,
            brandName: sourceBrand?.brand_name || sourceBrand?.name || 'Unknown Brand',
            brandIconUrl: sourceBrand?.icon_url,
            orgName: sourceOrg?.organization_name || sourceOrg?.name || 'Unknown Org'
          };
        });

        setAvailableSources(enrichedSources);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
    } finally {
      setLoadingSources(false);
    }
  };

  const handleSourceToggle = (sourceId) => {
    setSelectedSources(prev =>
      prev.includes(sourceId)
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleGenerateFromSources = async () => {
    if (selectedSources.length === 0) {
      alert('Please select at least one source');
      return;
    }

    if (!formData.brand) {
      alert('Please select a brand first');
      return;
    }

    try {
      setGeneratingContent(true);

      const selectedBrand = availableBrands.find(b => b.id === formData.brand);
      if (!selectedBrand) {
        alert('Please select a valid brand');
        return;
      }

      const sourcesToUse = availableSources
        .filter(s => selectedSources.includes(s._id))
        .map(s => ({
          type: s.source_type,
          content: s.content || s.original_url || s.filename,
          filename: s.filename
        }));

      const response = await axios.post('/api/content-drafts/create-from-sources', {
        brand_id: selectedBrand.id,
        organization_id: selectedBrand.organization_id,
        sources: sourcesToUse,
        content_type: selectedType.id,
        additional_instructions: formData.notes
      }, { withCredentials: true });

      if (response.data.success) {
        const generated = response.data.generated_content;
        const generatedText = generated.headline
          ? `${generated.headline}\n\n${generated.body}\n\n${generated.call_to_action}`
          : generated.body || generated.content || '';

        setFormData(prev => ({
          ...prev,
          caption: generatedText,
          notes: generated.notes || prev.notes
        }));

        alert('Content generated successfully! Review and edit below before submitting.');
        setShowSourcesSection(false);
        setSelectedSources([]);
      }
    } catch (error) {
      console.error('Error generating content from sources:', error);
      alert(error.response?.data?.error || 'Failed to generate content from sources');
    } finally {
      setGeneratingContent(false);
    }
  };
  */

  const handleTypeSelect = (type) => {
    setSelectedType(type);
  };

  const handleBack = () => {
    setSelectedType(null);
    setFormData({
      caption: '',
      brand: '',
      selectedTags: [],
      notes: ''
    });
    // COMMENTED OUT - No longer using sources
    // setSelectedSources([]);
    // setShowSourcesSection(false);
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => ({
      ...prev,
      selectedTags: prev.selectedTags.includes(tagId)
        ? prev.selectedTags.filter(t => t !== tagId)
        : [...prev.selectedTags, tagId]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Get the selected brand to find its organization_id
      const selectedBrand = availableBrands.find(b => b.id === formData.brand);
      if (!selectedBrand || !selectedBrand.organization_id) {
        await modal.alert('Please select a valid brand', 'Validation Error');
        return;
      }

      const titleDate = new Date().toISOString().split('T')[0];
      // Preserve selected tags as trailing hashtags - Content Preview already
      // extracts and displays hashtags found in the body text.
      const tagsSuffix = formData.selectedTags.length > 0
        ? `\n\n${formData.selectedTags.map(t => `#${t}`).join(' ')}`
        : '';

      await axios.post('/api/content-drafts/create-manual', {
        organization_id: selectedBrand.organization_id,
        brand_id: formData.brand,
        title: `${selectedType.name} - ${titleDate}`,
        content_type: selectedType.tags?.[0] || 'social',
        body: `${formData.caption}${tagsSuffix}`
      }, { withCredentials: true });
      modal.success('Content created! Find it in Review Content to finish and send to your client.');
      handleBack();
    } catch (error) {
      console.error('Error creating content:', error);
      modal.error(error.response?.data?.error || 'Failed to create content');
    }
  };

  // COMMENTED OUT - Source helper functions (no longer needed)
  /*
  const getSourceIcon = (type) => {
    const iconMap = {
      'url': 'link',
      'text': 'doc-text',
      'image': 'picture',
      'video': 'video',
      'audio': 'note-beamed',
      'transcription': 'mic',
      'meeting': 'mic'
    };
    return iconMap[type] || 'doc';
  };

  const getSourceTypeName = (type) => {
    const typeMap = {
      'meeting': 'TRANSCRIPT',
      'transcription': 'TRANSCRIPT',
      'url': 'URL',
      'text': 'TEXT',
      'image': 'IMAGE',
      'video': 'VIDEO',
      'audio': 'AUDIO'
    };
    return typeMap[type] || type.toUpperCase();
  };
  */

  // Check if tags should be hidden for this content type
  const shouldShowTags = () => {
    // Hide tags for content types that have mediaTypes (Instagram, TikTok, etc.)
    return !selectedType.mediaTypes;
  };

  // Render content type selection grid
  if (!selectedType) {
    return (
      <div className="content-creation-container">
        <div className="content-creation-header">
          <h2>Create New Content</h2>
          <p className="creation-subtitle">Select the type of content you want to create</p>
        </div>

        <div className="content-types-grid">
          {contentTypes.map((type) => (
            <button
              key={type.id}
              className="content-type-card"
              onClick={() => handleTypeSelect(type)}
              style={{ '--type-color': type.color }}
            >
              <div className="content-type-icon">
                <Icon name={type.icon} size="2.5rem" />
              </div>
              <h3>{type.name}</h3>
              <div className="create-arrow">
                <Icon name="go-to-link" size="1.25rem" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Render creation form
  return (
    <div className="content-creation-container">
      <div className="content-form-header">
        <button className="back-button" onClick={handleBack}>
          <Icon name="go-to-link" size="1.25rem" />
          <span>Back to Content Types</span>
        </button>
        <div className="form-title-section">
          <div className="form-icon" style={{ '--type-color': selectedType.color }}>
            <Icon name={selectedType.icon} size="2rem" />
          </div>
          <div>
            <h2>{selectedType.name}</h2>
            <p className="form-subtitle">Fill in the details for your content</p>
          </div>
        </div>
      </div>

      <form className="content-creation-form" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group full-width">
            <label htmlFor="brand">Brand *</label>
            <div className="brand-display" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem',
              backgroundColor: 'var(--color-surface-light-muted)',
              border: '1px solid var(--color-border-light-strong)',
              borderRadius: '4px',
              cursor: 'not-allowed'
            }}>
              {(() => {
                const selectedBrandObj = availableBrands.find(b => b.id === formData.brand);
                return (
                  <>
                    {selectedBrandObj?.icon_url ? (
                      <BrandIcon
                        src={selectedBrandObj.icon_url}
                        alt=""
                        style={{ width: '24px', height: '24px', borderRadius: '4px' }}
                      />
                    ) : (
                      <Icon name="tag" size="1.5rem" />
                    )}
                    <span style={{ fontWeight: '500' }}>
                      {selectedBrandObj?.name || 'No brand selected'}
                    </span>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="caption">Body Content *</label>
            <textarea
              id="caption"
              name="caption"
              value={formData.caption}
              onChange={handleInputChange}
              rows="6"
              placeholder="Enter your post content here..."
              required
            />
          </div>

          {/* Media Type Selection for Instagram/TikTok/YouTube */}
          {selectedType.mediaTypes && (
            <div className="form-group full-width">
              <label>Media Type</label>
              <div className="media-type-selection">
                {selectedType.mediaTypes.map(mediaType => (
                  <button
                    key={mediaType}
                    type="button"
                    className="media-type-button"
                  >
                    <Icon name={mediaType === 'Photo' ? 'picture' : 'video'} size="1rem" />
                    {mediaType}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Only show tags for certain content types */}
          {shouldShowTags() && (
            <div className="form-group full-width">
              <label>Tags (Optional)</label>
              <div className="tag-selection">
                {availableTags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    className={`tag-button ${formData.selectedTags.includes(tag.id) ? 'selected' : ''}`}
                    onClick={() => handleTagToggle(tag.id)}
                    style={{ '--tag-color': tag.color }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-group full-width">
            <label>Media Upload (Optional)</label>
            <div className="file-upload-area">
              <Icon name="image" size="2.5rem" />
              <p>Click to upload or drag and drop</p>
              <p className="upload-note">Images, videos, or documents</p>
              <input type="file" multiple accept="image/*,video/*" />
            </div>
          </div>

          {/* COMMENTED OUT - Source Selection Section (AI-based content generation)
          <div className="form-group full-width">
            <div className="source-section-header">
              <label>Add a Saved Source (Optional)</label>
              <button
                type="button"
                className="toggle-sources-btn"
                onClick={() => setShowSourcesSection(!showSourcesSection)}
              >
                <Icon name={showSourcesSection ? 'up-open' : 'down-open'} size="0.875rem" />
                {showSourcesSection ? 'Hide Sources' : 'Show Available Sources'}
              </button>
            </div>

            {showSourcesSection && (
              <div className="sources-selection-container">
                {loadingSources ? (
                  <div className="sources-loading-state">
                    <Icon name="spin6 animate-spin" size="1.5rem" />
                    <span>Loading sources...</span>
                  </div>
                ) : availableSources.length === 0 ? (
                  <div className="sources-empty-state">
                    <Icon name="doc" size="2rem" />
                    <p>No sources available</p>
                    <small>Add sources in the Sources panel first</small>
                  </div>
                ) : (
                  <>
                    <div className="sources-list-compact">
                      {availableSources.map(source => (
                        <div
                          key={source._id}
                          className={`source-item-compact ${selectedSources.includes(source._id) ? 'selected' : ''}`}
                          onClick={() => handleSourceToggle(source._id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSources.includes(source._id)}
                            onChange={() => {}}
                          />
                          <div className="source-item-details">
                            <div className="source-item-name">{source.name}</div>
                            <div className="source-item-meta">
                              <span className="source-org">{source.orgName}</span>
                              <span className="source-divider">•</span>
                              <div className="source-brand-with-icon">
                                {source.brandIconUrl ? (
                                  <BrandIcon src={source.brandIconUrl} alt={source.brandName} className="source-brand-icon" />
                                ) : (
                                  <div className="source-brand-icon-placeholder">
                                    {source.brandName.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <span className="source-brand">{source.brandName}</span>
                              </div>
                              <span className="source-divider">•</span>
                              <span className="source-type">{getSourceTypeName(source.source_type)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedSources.length > 0 && (
                      <button
                        type="button"
                        className="generate-from-sources-btn"
                        onClick={handleGenerateFromSources}
                        disabled={generatingContent}
                      >
                        {generatingContent ? (
                          <>
                            <Icon name="spin6 animate-spin" size="1rem" />
                            Generating Content...
                          </>
                        ) : (
                          <>
                            <Icon name="sparkles" size="1rem" />
                            Generate Content from {selectedSources.length} Source{selectedSources.length !== 1 ? 's' : ''}
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          */}
        </div>

        <div className="form-actions">
          <button type="button" className="btn-lg btn-secondary" onClick={handleBack}>
            Cancel
          </button>
          <button type="submit" className="btn-lg btn-primary" style={{ '--type-color': selectedType.color }}>
            Create {selectedType.name}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContentCreation;
